import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarRange, IndianRupee, Repeat } from "lucide-react";
import { api } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";
import { statusClass, statusLabel } from "@/lib/status";
import { currency } from "@/lib/format";
import type { Booking } from "@/types/domain";
import { LoadingGrid, StateView } from "@/components/ui/StateView";
import { CollectionsReport } from "./CollectionsReport";
import { InvoiceRegister } from "./InvoiceRegister";

const SOURCE_LABELS: Record<string, string> = {
  DIRECT_SITE: "Direct site", PHONE: "Phone", WALK_IN: "Walk-in", ADMIN_ADDED: "Admin added", SAAS_ADAPTER: "Channel manager",
};

export function ReportsPage() {
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const query = useQuery({ queryKey: ["bookings", propertyId, "reports"], queryFn: () => api<Booking[]>(`/bookings?propertyId=${propertyId}`) });
  const bookings = useMemo(() => query.data ?? [], [query.data]);

  const revenueBookings = useMemo(() => bookings.filter((b) => b.bookingStatus !== "CANCELLED" && b.bookingStatus !== "REJECTED"), [bookings]);
  const totalRevenue = useMemo(() => revenueBookings.reduce((sum, b) => sum + Number(b.totalAmount || 0), 0), [revenueBookings]);
  const avgBookingValue = revenueBookings.length ? totalRevenue / revenueBookings.length : 0;
  const cancelledCount = bookings.filter((b) => b.bookingStatus === "CANCELLED" || b.bookingStatus === "REJECTED" || b.bookingStatus === "AUTO_CANCELLED").length;
  const cancellationRate = bookings.length ? (cancelledCount / bookings.length) * 100 : 0;

  const revenueByMonth = useMemo(() => {
    const buckets = new Map<string, number>();
    revenueBookings.forEach((booking) => {
      const date = new Date(booking.checkInDate);
      const key = new Intl.DateTimeFormat("en-IN", { month: "short", year: "2-digit" }).format(date);
      buckets.set(key, (buckets.get(key) ?? 0) + Number(booking.totalAmount || 0));
    });
    return Array.from(buckets.entries()).slice(-6);
  }, [revenueBookings]);
  const maxMonthRevenue = Math.max(1, ...revenueByMonth.map(([, value]) => value));

  const statusBreakdown = useMemo(() => {
    const counts = new Map<Booking["bookingStatus"], number>();
    bookings.forEach((b) => counts.set(b.bookingStatus, (counts.get(b.bookingStatus) ?? 0) + 1));
    return Array.from(counts.entries()).sort(([, a], [, b]) => b - a);
  }, [bookings]);

  const sourceBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    bookings.forEach((b) => { const source = b.source ?? "DIRECT_SITE"; counts.set(source, (counts.get(source) ?? 0) + 1); });
    return Array.from(counts.entries()).sort(([, a], [, b]) => b - a);
  }, [bookings]);
  const maxSourceCount = Math.max(1, ...sourceBreakdown.map(([, value]) => value));

  if (query.isLoading) return <main className="page"><LoadingGrid /></main>;
  if (query.isError) return <main className="page"><StateView title="Couldn't load reports" message="Check your internet connection and try again." action={() => void query.refetch()} /></main>;

  return (
    <main className="page reports-page">
      <header className="page-head resource-head">
        <div>
          <h1>Reports</h1>
          <p className="page-head__meta">All-time figures for this property</p>
        </div>
      </header>

      <section className="kpi-strip reports-kpis" aria-label="Key figures">
        <div><small>Total revenue</small><strong className="num">{currency.format(totalRevenue)}</strong><span className="kpi-strip__note">{revenueBookings.length} revenue-generating bookings</span></div>
        <div><small>Total bookings</small><strong className="num">{bookings.length}</strong><span className="kpi-strip__note">All-time, this property</span></div>
        <div><small>Avg. booking value</small><strong className="num">{currency.format(avgBookingValue)}</strong><span className="kpi-strip__note">Per confirmed booking</span></div>
        <div><small>Cancellation rate</small><strong className="num">{cancellationRate.toFixed(1)}%</strong><span className="kpi-strip__note">{cancelledCount} of {bookings.length} bookings</span></div>
      </section>

      <CollectionsReport propertyId={propertyId} />
      <InvoiceRegister propertyId={propertyId} />

      <section className="dash-columns reports-grid">
        <div className="card reports-card reports-card--wide">
          <div className="card__head"><h2>Revenue by month</h2></div>
          {!revenueByMonth.length ? <div className="mini-empty"><IndianRupee /><p>No revenue booked yet.</p></div> : (
            <div className="report-bar-chart">
              {revenueByMonth.map(([month, value]) => (
                <div className="report-bar-chart__col" key={month} title={currency.format(value)}>
                  <span className="report-bar-chart__value">{currency.format(value)}</span>
                  <span className="report-bar-chart__bar" style={{ height: `${Math.max(6, (value / maxMonthRevenue) * 100)}%` }} />
                  <small>{month}</small>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card reports-card">
          <div className="card__head"><h2>Booking status</h2></div>
          {!statusBreakdown.length ? <div className="mini-empty"><Repeat /><p>No bookings yet.</p></div> : (
            <div className="report-status-list">
              {statusBreakdown.map(([status, count]) => (
                <div className="report-status-row" key={status}>
                  <span className={statusClass(status)}>{statusLabel(status)}</span>
                  <div className="report-status-row__track"><span className={`report-status-row__fill status--${status.toLowerCase()}`} style={{ width: `${(count / bookings.length) * 100}%` }} /></div>
                  <strong>{count}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="card reports-card">
        <div className="card__head"><h2>Booking sources</h2></div>
        {!sourceBreakdown.length ? <div className="mini-empty"><CalendarRange /><p>No bookings yet.</p></div> : (
          <div className="report-status-list">
            {sourceBreakdown.map(([source, count]) => (
              <div className="report-status-row" key={source}>
                <span className="report-status-row__label">{SOURCE_LABELS[source] ?? source}</span>
                <div className="report-status-row__track"><span className="report-status-row__fill report-status-row__fill--brand" style={{ width: `${(count / maxSourceCount) * 100}%` }} /></div>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
